import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { apiGet, apiPost } from '../../../services/api';

const PRICING_PACKAGES = [
  {
    package_type: 'single_listing',
    title: '1 Listing',
    price: '€149.00',
    description: '30-day active listing',
  },
  {
    package_type: 'bundle_5',
    title: '5-Pack',
    price: '€670.50',
    description: '5 listings · 10% bundle discount',
  },
  {
    package_type: 'bundle_10',
    title: '10-Pack',
    price: '€1,266.50',
    description: '10 listings · 15% bundle discount',
  },
  {
    package_type: 'bundle_20',
    title: '20-Pack',
    price: '€2,384.00',
    description: '20 listings · 20% bundle discount',
  },
];

function formatMoney(amount, currency = 'EUR') {
  if (amount === null || amount === undefined) return '—';
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
  }).format(amount);
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export default function CompanyBilling() {
  const { user, token, authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [creditsRemaining, setCreditsRemaining] = useState(0);
  const [activePackages, setActivePackages] = useState([]);
  const [extensionPrice, setExtensionPrice] = useState(119);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [accessDenied, setAccessDenied] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [extendLoadingId, setExtendLoadingId] = useState(null);
  const [invoiceDownloadingId, setInvoiceDownloadingId] = useState(null);

  const showCanceled = searchParams.get('canceled') === 'true';

  const tokenRef = useRef(token);
  const authLoadingRef = useRef(authLoading);

  useEffect(() => {
    tokenRef.current = token;
  }, [token]);
  useEffect(() => {
    authLoadingRef.current = authLoading;
  }, [authLoading]);

  const clearCheckoutParams = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('canceled');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleAuthError = useCallback(
    (res) => {
      if (res.status === 401) {
        navigate('/login', { replace: true });
        return true;
      }
      if (res.status === 403) {
        setAccessDenied(true);
        return true;
      }
      return false;
    },
    [navigate]
  );

  const loadBilling = useCallback(async () => {
    const currentToken = tokenRef.current;
    if (!currentToken) return;
    setLoading(true);
    setError('');
    setAccessDenied(false);
    try {
      const res = await apiGet('/api/v1/company/billing/history', currentToken);
      if (res.status === 401) {
        setError('Session expired. Please refresh the page.');
        return;
      }
      if (res.status === 403) {
        setAccessDenied(true);
        return;
      }
      if (!res.ok) {
        setError('Could not load billing data. Please try again.');
        return;
      }
      const data = await res.json();
      setCreditsRemaining(data?.data?.credits_remaining ?? 0);
      setActivePackages(data?.data?.active_packages ?? []);
      setExtensionPrice(Number(data?.data?.extension_price) || 119);
      setPurchases(data?.data?.purchases ?? []);
    } catch {
      setError('Could not load billing data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!token || !user) {
      navigate('/login', { replace: true });
      return;
    }
    if (user.role !== 'company') {
      navigate('/dashboard', { replace: true });
      return;
    }
    loadBilling();
  }, [authLoading, token, user, navigate, loadBilling]);

  useEffect(() => {
    function handleFocus() {
      if (!tokenRef.current) return;
      if (authLoadingRef.current) return;
      loadBilling();
    }

    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [loadBilling]);

  async function handleBuyNow(packageType) {
    if (!token) return;
    setCheckoutLoading(packageType);
    setError('');
    try {
      const res = await apiPost(
        '/api/v1/company/checkout',
        { package_type: packageType },
        token
      );
      if (handleAuthError(res)) return;
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data?.message || 'Could not start checkout. Please try again.');
        return;
      }
      const checkoutUrl = data?.data?.checkout_url;
      if (checkoutUrl) {
        const newTab = window.open(checkoutUrl, '_blank', 'noopener,noreferrer');
        if (!newTab) {
          setError(
            'Your browser blocked the payment window. Please allow popups for this site and try again.'
          );
        }
        return;
      }
      setError('Could not start checkout. Please try again.');
    } catch {
      setError('Could not start checkout. Please try again.');
    } finally {
      setCheckoutLoading(null);
    }
  }

  async function handleExtendPackage(packageId) {
    if (!token || !packageId) return;
    setExtendLoadingId(packageId);
    setError('');
    try {
      const res = await apiPost(`/api/v1/company/packages/${packageId}/extend`, {}, token);
      if (handleAuthError(res)) return;
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data?.message || 'Could not start extension checkout. Please try again.');
        return;
      }
      const checkoutUrl = data?.data?.checkout_url;
      if (checkoutUrl) {
        const newTab = window.open(checkoutUrl, '_blank', 'noopener,noreferrer');
        if (!newTab) {
          setError(
            'Your browser blocked the payment window. Please allow popups for this site and try again.'
          );
        }
        return;
      }
      setError('Could not start extension checkout. Please try again.');
    } catch {
      setError('Could not start extension checkout. Please try again.');
    } finally {
      setExtendLoadingId(null);
    }
  }

  async function handleInvoiceDownload(invoiceId) {
    if (!token || !invoiceId) return;
    setInvoiceDownloadingId(invoiceId);
    setError('');
    try {
      const res = await apiGet(`/api/v1/company/invoices/${invoiceId}/download`, token);
      if (handleAuthError(res)) return;
      if (!res.ok) {
        setError('Could not download invoice. Please try again.');
        return;
      }
      const data = await res.json();
      const url = data?.data?.url;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        setError('Could not download invoice. Please try again.');
      }
    } catch {
      setError('Could not download invoice. Please try again.');
    } finally {
      setInvoiceDownloadingId(null);
    }
  }

  if (authLoading || (loading && !accessDenied)) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" role="status" data-testid="rwm-billing-loading">
          <span className="visually-hidden">Loading…</span>
        </Spinner>
      </Container>
    );
  }

  if (accessDenied) {
    return (
      <Container className="py-4 rwm-billing">
        <Alert variant="danger" data-testid="rwm-billing-access-denied">
          Billing is only available to company administrators.
        </Alert>
        <Button as={Link} to="/company/dashboard" variant="outline-secondary">
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  return (
    <Container className="py-4 rwm-billing">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
        <h1 className="h3 mb-0 rwm-billing__title">Billing &amp; Packages</h1>
        <Button
          as={Link}
          to="/company/dashboard"
          variant="outline-secondary"
          data-testid="rwm-billing-back-dashboard"
        >
          Back to Dashboard
        </Button>
      </div>

      {showCanceled ? (
        <Alert
          variant="warning"
          className="mb-4"
          dismissible
          onClose={clearCheckoutParams}
          data-testid="rwm-billing-canceled-alert"
        >
          Payment was canceled. No charges were made.
        </Alert>
      ) : null}

      {error ? (
        <Alert variant="danger" className="mb-4" data-testid="rwm-billing-error">
          {error}
        </Alert>
      ) : null}

      <div className="rwm-billing__credits-summary mb-4">
        <h2 className="h5 mb-2">Available listing credits</h2>
        <p
          className="rwm-billing__credits-value mb-0"
          data-testid="rwm-billing-credits-remaining"
        >
          {creditsRemaining}
        </p>
        <p className="text-muted small mb-0">
          Total unused credits across active packages (quantity minus used).
        </p>
      </div>

      <h2 className="h5 mb-3">Your active packages</h2>
      {activePackages.length === 0 ? (
        <p className="text-muted mb-4" data-testid="rwm-billing-active-packages-empty">
          No active packages.
        </p>
      ) : (
        <div className="mb-5" data-testid="rwm-billing-active-packages">
          <div className="table-responsive">
            <Table className="rwm-billing__active-packages-table align-middle mb-0">
              <thead>
                <tr>
                  <th>Package</th>
                  <th>Credits left</th>
                  <th>Expires</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {activePackages.map((pkg) => (
                  <tr
                    key={pkg.id}
                    data-testid={`rwm-billing-active-package-${pkg.id}`}
                  >
                    <td>
                      <div>{pkg.package_label || pkg.plan}</div>
                      {pkg.extended_at ? (
                        <div
                          className="small text-muted"
                          data-testid={`rwm-billing-extended-date-${pkg.id}`}
                        >
                          Extended on {formatDate(pkg.extended_at)}
                        </div>
                      ) : null}
                    </td>
                    <td>{pkg.credits_remaining}</td>
                    <td>{formatDate(pkg.expires_at)}</td>
                    <td>
                      {pkg.can_extend ? (
                        <Button
                          variant="outline-warning"
                          size="sm"
                          disabled={extendLoadingId !== null}
                          onClick={() => handleExtendPackage(pkg.id)}
                          data-testid={`rwm-billing-extend-${pkg.id}`}
                        >
                          {extendLoadingId === pkg.id
                            ? 'Opening…'
                            : `Extend 30 days — €${extensionPrice.toFixed(2)}`}
                        </Button>
                      ) : (
                        <span className="text-muted small">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </div>
      )}

      <h2 className="h5 mb-3">Purchase listing credits</h2>
      <Row className="g-3 mb-5">
        {PRICING_PACKAGES.map((pkg) => (
          <Col key={pkg.package_type} md={6} lg={3}>
            <Card className="rwm-billing__card h-100">
              <Card.Body className="d-flex flex-column">
                <Card.Title className="h6">{pkg.title}</Card.Title>
                <p className="rwm-billing__price mb-2">{pkg.price}</p>
                <p className="text-muted small flex-grow-1">{pkg.description}</p>
                <Button
                  variant="info"
                  className="text-white mt-auto"
                  disabled={checkoutLoading !== null}
                  onClick={() => handleBuyNow(pkg.package_type)}
                  data-testid={`rwm-billing-buy-${pkg.package_type}`}
                >
                  {checkoutLoading === pkg.package_type ? 'Redirecting…' : 'Buy Now'}
                </Button>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <h2 className="h5 mb-3">Billing history</h2>
      {purchases.length === 0 ? (
        <p className="text-muted" data-testid="rwm-billing-history-empty">
          No purchases yet.
        </p>
      ) : (
        <div className="table-responsive">
          <Table className="rwm-billing__table align-middle mb-0">
            <thead>
              <tr>
                <th>Date</th>
                <th>Package</th>
                <th>Amount</th>
                <th>Invoice</th>
              </tr>
            </thead>
            <tbody>
              {purchases.map((row) => (
                <tr key={`${row.invoice_id ?? 'pkg'}-${row.package_id}-${row.purchased_at}`}>
                  <td>{formatDate(row.purchased_at)}</td>
                  <td>{row.package_label || row.package_type}</td>
                  <td>{formatMoney(row.amount, row.currency)}</td>
                  <td>
                    {row.invoice_id ? (
                      <Button
                        variant="outline-info"
                        size="sm"
                        disabled={invoiceDownloadingId === row.invoice_id}
                        onClick={() => handleInvoiceDownload(row.invoice_id)}
                        data-testid={`rwm-billing-invoice-download-${row.invoice_id}`}
                      >
                        {invoiceDownloadingId === row.invoice_id
                          ? 'Loading…'
                          : 'Download'}
                      </Button>
                    ) : (
                      <span className="text-muted small">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </Container>
  );
}
